import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { openSignedDownload } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { FileText, ShieldCheck, Clock } from "lucide-react";

/**
 * Modal wizard that captures a policyholder's typed legal name + attestation,
 * POSTs /api/claims/{id}/certify, and then triggers the signed Proof-of-Loss
 * PDF download. The modal shows any pre-existing certifications (audit trail).
 */
export default function ProofOfLossDialog({ open, onOpenChange, claim }) {
  const [typedName, setTypedName] = useState("");
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [priorCerts, setPriorCerts] = useState([]);

  useEffect(() => {
    if (!open || !claim?.id) return;
    setTypedName("");
    setAttested(false);
    api
      .get(`/claims/${claim.id}/certifications`)
      .then((r) => setPriorCerts(r.data || []))
      .catch(() => setPriorCerts([]));
  }, [open, claim?.id]);

  const nameMatches =
    typedName.trim().toLowerCase() === (claim?.full_name || "").trim().toLowerCase();
  const canSubmit = nameMatches && attested && !submitting;

  const onSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await api.post(`/claims/${claim.id}/certify`, {
        typed_name: typedName.trim(),
        attestation: true,
      });
      toast.success("Certified — generating signed PDF…");
      await openSignedDownload(
        "proof_of_loss_pdf",
        claim.id,
        `/claims/${claim.id}/proof-of-loss.pdf`,
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Certification failed");
    } finally {
      setSubmitting(false);
    }
  };

  const onDownloadUnsigned = () => {
    openSignedDownload(
      "proof_of_loss_pdf",
      claim.id,
      `/claims/${claim.id}/proof-of-loss.pdf`,
    );
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="pol-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit'] flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#C25934]" />
            Certify & e-sign Proof of Loss
          </DialogTitle>
          <DialogDescription className="text-xs text-gray-600 leading-relaxed">
            Under the federal E-SIGN Act (15 U.S.C. §7001), typing your full
            legal name below creates a legally binding signature. Your IP
            address, timestamp, and browser details will be recorded in the
            audit certificate embedded in the PDF.
          </DialogDescription>
        </DialogHeader>

        {priorCerts.length > 0 && (
          <div className="rounded-md border border-[#E5E7EB] bg-[#F3F4F6] p-3 text-xs text-gray-700 space-y-1">
            <div className="font-semibold text-[#1E3F33] flex items-center gap-1">
              <Clock className="w-3.5 h-3.5" />
              Prior certifications on file
            </div>
            {priorCerts.slice(0, 3).map((c) => (
              <div key={c.id} className="font-mono truncate">
                {c.created_at?.slice(0, 19)} · /s/ {c.typed_name} · {c.ip}
              </div>
            ))}
          </div>
        )}

        <div className="space-y-3 mt-2">
          <div>
            <Label htmlFor="legal-name" className="text-xs text-gray-600">
              Legal name on file
            </Label>
            <p
              className="text-sm font-medium text-[#1E3F33] mt-0.5"
              data-testid="pol-legal-name"
            >
              {claim?.full_name}
            </p>
          </div>
          <div>
            <Label htmlFor="typed-name" className="text-xs text-gray-600">
              Type your full legal name to sign
            </Label>
            <Input
              id="typed-name"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder={claim?.full_name || ""}
              autoComplete="off"
              className="mt-1 font-['Times'] italic text-base"
              data-testid="pol-typed-name"
            />
            {typedName && !nameMatches && (
              <p className="text-xs text-red-600 mt-1">
                Must match exactly the legal name above.
              </p>
            )}
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 leading-snug">
            <Checkbox
              checked={attested}
              onCheckedChange={(v) => setAttested(Boolean(v))}
              data-testid="pol-attestation"
            />
            <span>
              I declare under penalty of perjury that the foregoing is true and
              that no material fact has been omitted. I agree to electronically
              sign this Sworn Proof of Loss.
            </span>
          </label>
        </div>

        <DialogFooter className="gap-2 sm:gap-2 mt-3">
          <Button
            type="button"
            variant="outline"
            onClick={onDownloadUnsigned}
            data-testid="pol-skip-esign"
          >
            <FileText className="w-4 h-4" />
            Download blank (sign on paper)
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={!canSubmit}
            className="bg-[#C25934] hover:bg-[#a94a2b] text-white disabled:opacity-50"
            data-testid="pol-submit"
          >
            <ShieldCheck className="w-4 h-4" />
            {submitting ? "Signing…" : "Certify & download signed PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
